import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search, X } from 'lucide-react';

/**
 * CompactConditionSelect
 * A sleek, compact dropdown for selecting conditions/types in tables and modals.
 * Uses createPortal with position:fixed so it is NEVER clipped by parent table/card overflow-x-auto boundaries.
 */
export const CompactConditionSelect = ({
  value = '',
  onChange,
  options = [],
  placeholder = 'Select type',
  disabled = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownRect, setDropdownRect] = useState(null);

  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);
  const selectedItemRef = useRef(null);

  // Measure button position for fixed portal positioning
  const updateDropdownRect = useCallback(() => {
    if (triggerRef.current && isOpen) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < 220 && rect.top > 220;

      setDropdownRect({
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
        openAbove
      });
    } else {
      setDropdownRect(null);
    }
  }, [isOpen]);

  // Update rect on open and listen to scroll/resize
  useEffect(() => {
    if (!isOpen) return;
    updateDropdownRect();

    const onScrollOrResize = () => updateDropdownRect();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen, updateDropdownRect]);

  // Close when clicking outside or pressing Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      const clickedTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const clickedMenu = menuRef.current && menuRef.current.contains(e.target);

      if (!clickedTrigger && !clickedMenu) {
        setIsOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Focus search input on open
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 50);
    }
  }, [isOpen]);

  // Scroll to selected item on open
  useEffect(() => {
    if (isOpen && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen]);

  const handleSelect = (val) => {
    if (onChange) {
      onChange(val);
    }
    setIsOpen(false);
  };

  const filteredOptions = options.filter((opt) =>
    opt.toLowerCase().includes(searchTerm.trim().toLowerCase())
  );

  return (
    <div className={`relative w-full ${className}`}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className={`w-full px-2 py-1.5 bg-white border rounded-lg text-xs text-left flex items-center justify-between gap-1 transition-all cursor-pointer ${
          isOpen
            ? 'border-blue-600 ring-1 ring-blue-600 shadow-xs'
            : 'border-slate-200 hover:border-slate-300'
        } ${disabled ? 'opacity-50 cursor-not-allowed bg-slate-50' : ''}`}
      >
        <span className={`truncate ${value ? 'font-medium text-slate-800' : 'text-slate-400'}`}>
          {value || placeholder}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-150 ${
            isOpen ? 'rotate-180 text-blue-600' : ''
          }`}
        />
      </button>

      {/* Portal Dropdown Menu: rendered directly into document.body to avoid table overflow-x-auto clipping */}
      {isOpen && dropdownRect && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            left: Math.max(10, Math.min(dropdownRect.left, window.innerWidth - Math.max(dropdownRect.width, 220) - 10)),
            width: Math.max(dropdownRect.width, 220),
            ...(dropdownRect.openAbove
              ? { bottom: window.innerHeight - dropdownRect.top + 4 }
              : { top: dropdownRect.bottom + 4 }),
            zIndex: 99999
          }}
          className="bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden animate-in fade-in-50 zoom-in-95 duration-100 font-sans"
        >
          {/* Quick Search Bar */}
          <div className="p-1.5 border-b border-slate-100 bg-slate-50/70 flex items-center gap-1.5">
            <Search className="w-3.5 h-3.5 text-slate-400 ml-1 shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search condition..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-1.5 py-1 text-xs bg-transparent focus:outline-none text-slate-800 placeholder:text-slate-400"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="p-0.5 text-slate-400 hover:text-slate-600 cursor-pointer"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Scrollable list capped at max-h-48 (~192px) for a compact, clean look */}
          <div className="max-h-48 overflow-y-auto divide-y divide-slate-50 py-1 scrollbar-thin">
            {/* Clear option */}
            <button
              type="button"
              onClick={() => handleSelect('')}
              className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center justify-between cursor-pointer ${
                !value
                  ? 'bg-blue-50 text-blue-700 font-semibold'
                  : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              <span>-- None / Clear --</span>
              {!value && <Check className="w-3.5 h-3.5 text-blue-600" />}
            </button>

            {/* If currently selected value is custom or legacy, show it */}
            {value && !options.includes(value) && (
              <button
                type="button"
                onClick={() => handleSelect(value)}
                className="w-full text-left px-3 py-1.5 text-xs bg-blue-50 text-blue-700 font-semibold flex items-center justify-between cursor-pointer"
              >
                <span className="truncate">{value}</span>
                <Check className="w-3.5 h-3.5 text-blue-600" />
              </button>
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isSelected = value === opt;
                return (
                  <button
                    key={opt}
                    ref={isSelected ? selectedItemRef : null}
                    type="button"
                    onClick={() => handleSelect(opt)}
                    className={`w-full text-left px-3 py-1.5 text-xs transition-colors flex items-center justify-between cursor-pointer ${
                      isSelected
                        ? 'bg-blue-50 text-blue-700 font-semibold'
                        : 'text-slate-700 hover:bg-blue-50/70 hover:text-blue-600'
                    }`}
                  >
                    <span className="truncate">{opt}</span>
                    {isSelected && <Check className="w-3.5 h-3.5 text-blue-600 shrink-0 ml-2" />}
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-2.5 text-[11px] text-slate-400 text-center">
                No matching condition found
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CompactConditionSelect;
